import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useState } from 'react';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { getTableScrollbar } from '../../../util/scrollbar';

const textFiledStyle = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.background.field,
        // borderRadius: 10,
        borderRadius: ({ borderRadius }) => (borderRadius ? borderRadius : 10),
        '& fieldset': {
            border: ({ noBorder }) => (noBorder ? 'none' : ''),
            borderRadius: ({ borderRadius }) => borderRadius,
        },
        '& input::placeholder': {
            fontSize: ({ smallerPlaceholder }) => (smallerPlaceholder ? '.95rem' : '1rem'),
        },
        '& input[type=number]': {
            '-moz-appearance': 'textfield',
        },
        '& input[type=number]::-webkit-outer-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
        '& input[type=number]::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
        '& .MuiOutlinedInput-input': {
            //   height: ({isEyonix}) => isEyonix ? 14 : 'auto',
        },
    },
    font: {
        fontFamily: 'Inter',
        fontWeight: 500,
        '&:-webkit-autofill': {
            '-webkit-box-shadow': `0 0 0 100px ${theme.palette.background.field} inset`,
            // '-webkit-text-fill-color': '#fff',
        },
    },
    paperBackground: {
        backgroundColor: theme.palette.background.paperDarker,
    },
    inPatientBackground: {
        backgroundColor: `${theme.palette.inPatient.inputBackground} !important`,
    },
}));

const AgyleTextField = forwardRef((props, ref) => {
    const isEyonix = process.env.BUILD_MODULE === 'main';
    const classes = textFiledStyle({
        noBorder: props.noBorder,
        smallerPlaceholder: props.smallerPlaceholder,
        borderRadius: props.borderRadius,
        isEyonix,
    });
    const [textValue, setTextValue] = useState(!!props.value ? props.value : '');
    // console.log("AgyleTextField inside InputProps", props);
    // console.log("AgyleTextField inside InputProps", props.name);
    useEffect(() => {
        if (props.debounce) {
            setTextValue(props.value);
        }
    }, [props.value]);

    const handleChange = (event) => {
        const val = event.target.value;
        if (props.type === 'ambulance') {
            if (/^(\d*-\d*|\d*)$/.test(val)) {
                if (props.debounce) {
                    setTextValue(val);
                } else {
                    props.onChange(event);
                }
            }
        } else {
            if (props.debounce) {
                setTextValue(val);
            } else {
                props.onChange(event);
            }
        }
    };

    const handleBlur = (event) => {
        if (props.debounce) {
            props.onChange(event);
        }
        if (props.onBlur) props.onBlur(event);
    };

    return (
        <TextField
            ref={ref}
            name={props.name}
            label={props.label}
            disabled={props.disabled}
            size={props.size}
            value={props.debounce ? textValue : props.value}
            error={props.error}
            className={clsx(classes.root, props?.withNewBackground && classes.inPatientBackground, props.textFieldClass)}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={props.onFocus}
            onKeyDown={props.onKeyDown}
            multiline={props.multiline}
            rows={props.rows}
            minRows={props.multiline ? props.minRows : null}
            maxRows={props.multiline ? props.maxRows : null}
            fullWidth={props.fullWidth}
            type={props.type}
            autoFocus={props.autoFocus}
            select={props.select}
            style={props.style}
            required={props.required}
            children={props.children}
            placeholder={props.placeholder}
            helperText={props.helperText}
            pattern={props.pattern}
            inputMode={props.inputMode}
            autoComplete={props.autoComplete}
            inputRef={props.inputRef}
            sx={props.sx}
            InputLabelProps={{
                shrink: props.shrink || (props.debounce ? !isEmpty(textValue) : !isEmpty(props.value)),
            }}
            InputProps={{
                classes: {
                    input: clsx(
                        classes.font,
                        props.inputPropsClasses,
                        getTableScrollbar(),
                        props?.paperBackground && classes.paperBackground,
                    ),
                },
                endAdornment: props.endAdornment || null,
                startAdornment: props.startAdornment || null,
                readOnly: props.readOnly,
                inputMode: props.inputMode,
                step: props.step,
                // textAlign: props.textAlign,
            }}
            inputProps={{
                maxLength: props.maxLength,
                minLength: props.minLength,
                readOnly: props.readOnly,
                inputMode: props.inputMode,
                step: props.step,
                min: props.min,
                max: props.max,
                type: props.type,
                style: { textAlign: props.textAlign },
                ...props.inputProps,
            }}
            id={props.id}
        />
    );
});

export default AgyleTextField;

AgyleTextField.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    helperText: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    endAdornment: PropTypes.any,
    startAdornment: PropTypes.any,
    noBorder: PropTypes.bool,
    multiline: PropTypes.bool,
    rows: PropTypes.number,
    fullWidth: PropTypes.bool,
    debounce: PropTypes.bool,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    minRows: PropTypes.number,
    maxRows: PropTypes.number,
    error: PropTypes.bool,
    textFieldClass: PropTypes.any,
    inputPropsClasses: PropTypes.any,
    shrink: PropTypes.bool,
    inputProps: PropTypes.object,
    required: PropTypes.bool,
    min: PropTypes.number,
    max: PropTypes.number,
    paperBackground: PropTypes.bool,
    withNewBackground: PropTypes.bool,
    smallerPlaceholder: PropTypes.bool,
    type: PropTypes.string,
    borderRadius: PropTypes.number,
    classRoot: PropTypes.any,
    autoComplete: PropTypes.string,
    id: PropTypes.string,
};

AgyleTextField.defaultProps = {
    name: '',
    label: '',
    helperText: '',
    value: '',
    placeholder: '',
    multiline: false,
    noBorder: true,
    fullWidth: false,
    debounce: false,
    disabled: false,
    error: false,
    readOnly: false,
    shrink: false,
    size: 'small',
    required: false,
    paperBackground: false,
    withNewBackground: false,
    smallerPlaceholder: false,
    type: 'text',
    borderRadius: 6,
    autoComplete: '',
};
